import { useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import {
  DEFAULT_SPREAD,
  SPREADS,
  TOPICS,
  TOPIC_LABELS,
  getDefaultSpread,
  type DrawnCard,
  type Mbti,
  type Topic,
  cleanInterpretation,
} from '@tarot/shared';
import { CrisisBlockedError, QuotaExceededError, streamInterpret } from '../../lib/api';
import { QuotaGate, useQuotaBlocked, type QuotaBlock } from '../quota/QuotaNotice';
import { useRefreshQuota } from '../quota/useQuota';
import { useTrack } from '../../lib/track';
import { usePublishReading } from './useAutoSave';
import { useScrollToReading, useScrollToCta } from './followScroll';
import { SaveCta } from './SaveCta';
import { ReviewCta } from './ReviewCta';
import { TossBanner } from '../../components/TossBanner';
import { CardFan, slotGridClass, slotLabelClass } from '../../components/CardFan';
import { TarotCard } from '../../components/TarotCard';
import { TopicIcon } from '../../components/TopicIcon';
import { CaptureSignature, ReadingActions } from '../../components/ReadingActions';
import { MagicText } from '../../components/MagicText';
import { CardMeaning } from '../../components/CardMeaning';
import { BackIcon, CaretIcon, ShuffleIcon } from '../../components/icons';
import { HomeLink } from '../../components/HomeLink';
import { ActionStack, PrimaryButton, SecondaryButton } from '../../components/Buttons';
import { MbtiField } from '../../components/MbtiPicker';
import { ConcernField } from '../../components/ConcernField';
import { CrisisNotice } from '../../components/CrisisNotice';
import { useMe } from '../auth/useMe';
import { isToss } from '../../lib/platform';

type Step = 'topic' | 'pick' | 'reading';

/** 주제 칩 모양 — 토스에서는 이 칩이 곧 [주제 다시 고르기] 버튼이라 한 곳에서 정한다 */
const TOPIC_CHIP_CLASS =
  'flex items-center gap-2.5 rounded-full border border-gold/30 bg-gold/10 px-5 py-2.5 text-base';

/**
 * '나 혼자' 타로 흐름: 주제 선택 → 펼쳐진 덱에서 직접 뽑기(CardFan) → SSE 해석.
 */
export function SoloPage() {
  useTrack('menu:solo');
  const { me } = useMe();
  const [step, setStep] = useState<Step>('topic');
  // MBTI(선택): 회원은 내 정보에 저장된 값이 기본 선택되고, 이 리딩에서만 바꿀 수도 있음
  const [mbtiPick, setMbtiPick] = useState<Mbti | null>(null);
  const [mbtiOpen, setMbtiOpen] = useState(false);
  const [mbtiTouched, setMbtiTouched] = useState(false);
  const mbti = mbtiPick ?? undefined;
  const [topic, setTopic] = useState<Topic | null>(null);
  // 고민 한 줄(선택) — 카드를 뽑기 전에 받아 해석 프롬프트에 전달
  const [concern, setConcern] = useState('');
  const [cards, setCards] = useState<DrawnCard[]>([]);
  const [fanKey, setFanKey] = useState(0); // 다시 섞기 = CardFan 리마운트
  const [text, setText] = useState('');
  // 본문이 마지막 어절까지 다 드러났는지 — 스트림 종료와는 별개다.
  // 펼치는 중에 [이미지]를 누르면 잘린 해석이 그대로 이미지로 굳는다.
  const [revealed, setRevealed] = useState(false);
  // 공유·히스토리 저장용 메타 (모델명 + 토큰 수) — 스트림 완료 통지에서 받는다
  const [meta, setMeta] = useState<{ model?: string; tokensIn?: number; tokensOut?: number }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 하루 이용 횟수 초과 안내 (광고로 이어서 보기)
  const [quotaBlock, setQuotaBlock] = useState<QuotaBlock | null>(null);
  /** 위기 발화로 서버가 해석을 되돌림 — 카드 대신 상담 창구를 보여준다 (shared safety.ts) */
  const [crisis, setCrisis] = useState(false);
  const quotaBlocked = useQuotaBlocked(quotaBlock); // 소진 시 뽑기·해석 진입 차단
  const refreshQuota = useRefreshQuota();
  const abortRef = useRef<AbortController | null>(null);
  const captureRef = useRef<HTMLDivElement | null>(null); // 공유 캡처 영역 (카드+해석)

  // 주제별 기본 스프레드: 오늘의 운세 → 원 카드, 그 외 → 3장 (shared getDefaultSpread)
  const spreadId = topic ? getDefaultSpread('solo', topic) : DEFAULT_SPREAD;
  const spread = SPREADS[spreadId];
  const positionLabels = spread.positions.map((p) => p.nameKo);

  // 회원이 내 정보에 저장해 둔 MBTI를 기본 선택으로 (직접 고른 뒤에는 덮어쓰지 않음)
  useEffect(() => {
    if (me?.mbti && !mbtiTouched) setMbtiPick(me.mbti);
  }, [me, mbtiTouched]);

  const reshuffle = () => {
    setCards([]);
    setFanKey((k) => k + 1);
  };

  /**
   * 단계가 바뀌면 새 화면이므로 맨 위에서 시작한다.
   * 주제 목록에서 아래로 스크롤해 [카드 뽑으러 가기]를 누르면 그 스크롤 위치가 그대로 남아
   * 덱 화면이 중간부터 보인다. 라우트 이동이 아니라 ScrollToTopOnNavigate가 잡지 못한다.
   * 다시 섞기(아래)와 달리 즉시 이동 — 사파리는 문서가 크게 바뀌는 중의 smooth 스크롤을 자주 취소한다.
   */
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  /**
   * 다시 섞으면 덱이 화면 위쪽에서 새로 펼쳐지므로 시선을 덱으로 되돌린다.
   * 클릭 핸들러에서 바로 스크롤하면 소용이 없다 — 직후 뽑은 카드·버튼이 사라지며
   * 문서가 짧아지고, 브라우저가 스크롤 위치를 다시 고정하면서 부드러운 스크롤이 취소된다.
   * 그래서 DOM이 줄어든 다음 프레임에 실행한다.
   */
  const firstFan = useRef(true);
  useEffect(() => {
    if (firstFan.current) {
      firstFan.current = false;
      return;
    }
    const id = requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
    return () => cancelAnimationFrame(id);
  }, [fanKey]);

  const onInterpret = async () => {
    /**
     * ⚠️ 여기서 quotaBlocked로 막으면 안 된다. 광고 게이트의 onAdWatched가 이 함수를 부르는데,
     * 그 시점 클로저의 quotaBlocked는 아직 true라 **광고를 보고도 해석이 시작되지 않는다**
     * (2026-08-21 e2e-ads로 실측 — 3회 재현). 버튼 잠금은 disabled가 담당하고,
     * 진짜 막힌 상태면 서버가 402를 다시 주므로 아래 catch가 게이트로 되돌린다.
     */
    if (!topic || cards.length !== spread.positions.length) return;
    setBusy(true);
    setError(null);
    setQuotaBlock(null);
    setCrisis(false);
    setText('');
    setRevealed(false);
    setStep('reading');
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      await streamInterpret(
        {
          mode: 'solo',
          topic,
          spreadId,
          participants: [{ seat: 0, nickname: '나', cards, mbti }],
          ...(concern.trim() ? { concern: concern.trim() } : {}),
        },
        {
          onDelta: (t) => setText((prev) => prev + t),
          onDone: (m) => {
            setMeta(m);
            // 끝난 뒤 한 번 정리 — 모델이 문장 끝에 잡음 토큰을 붙여 보내는 일이 있다
            setText(cleanInterpretation);
          },
          onError: (m) => setError(m),
        },
        ac.signal,
      );
    } catch (e) {
      if (e instanceof CrisisBlockedError) {
        // 해석 대신 상담 안내. 뽑아둔 카드는 그대로 두고 뽑기 화면으로 되돌린다
        setCrisis(true);
        setStep('pick');
      } else if (e instanceof QuotaExceededError) {
        // 한도 초과 — 뽑기 화면으로 되돌리고 늘리는 방법을 안내
        setQuotaBlock({ message: e.message, status: e.status, reason: e.reason });
        setStep('pick');
      } else if (!(e instanceof DOMException && e.name === 'AbortError')) {
        setError(e instanceof Error ? e.message : '오류가 발생했어요');
      }
    } finally {
      setBusy(false);
      refreshQuota();
    }
  };

  /** 주제 선택으로 되돌아간다 — 뽑아둔 카드는 버린다(주제가 바뀌면 스프레드가 달라진다) */
  const backToTopic = () => {
    reshuffle();
    setStep('topic');
  };

  const reset = () => {
    abortRef.current?.abort();
    setStep('topic');
    setTopic(null);
    setConcern('');
    reshuffle();
    setText('');
    setRevealed(false);
    setError(null);
    setCrisis(false);
  };

  // 해석 완료 → 회원이면 지난 타로 자동 저장 (공유 버튼은 같은 shareId 재사용)
  const shareData =
    step === 'reading' && !busy && !error && text
      ? {
          mode: 'solo' as const,
          topic: topic!,
          spreadId,
          participants: [{ seat: 0, nickname: '나', cards }],
          interpretation: text,
          ...meta,
        }
      : null;
  const savedShareId = usePublishReading(shareData);

  // 해석이 시작되면 본문 첫 줄이 화면 맨 위에 오도록 (카드가 화면을 다 채워 글을 놓치는 걸 막는다)
  const readingRef = useScrollToReading();
  // 카드를 다 뽑으면 [해석 듣기]가 화면 한가운데 오도록 (버튼이 카드 아래라 안 보인다)
  const ctaRef = useScrollToCta();

  return (
    <div className="flex flex-1 flex-col gap-6">
      <AnimatePresence mode="wait">
        {step === 'topic' && (
          <motion.section key="topic" {...fade} className="flex flex-col gap-4">
            <h2 className="text-2xl font-bold">어떤 이야기가 궁금하세요?</h2>
            {/* 주제 7개 = 4줄. 아래 [카드 뽑으러 가기] 버튼이 접히지 않도록 타일을 낮게 유지한다 */}
            <ul className="grid grid-cols-2 gap-2.5">
              {TOPICS.map((t) => (
                <li key={t} className={TOPIC_LABELS[t].featured ? 'col-span-2' : ''}>
                  <button
                    onClick={() => setTopic(t)}
                    className={`flex w-full flex-col items-center gap-0.5 rounded-2xl border px-3 py-2.5 text-center transition ${
                      topic === t
                        ? 'border-gold bg-gold/15'
                        : 'border-white/10 bg-white/5 hover:border-gold/50'
                    }`}
                  >
                    <TopicIcon topic={t} className="h-9 w-9 text-gold" />
                    <span className="text-sm font-semibold">{TOPIC_LABELS[t].ko}</span>
                  </button>
                </li>
              ))}
            </ul>
            {topic && <ConcernField value={concern} onChange={setConcern} />}
            {/* 소진 시 카드를 뽑기 전에 막는다 — 다 뽑고 나서 막히면 헛수고가 됨 */}
            {/*
              ⚠️ 주제를 고르기 **전에도** 이 게이트가 보인다. 광고를 봤다고 무작정 뽑기 화면으로
              넘기면 주제 없이 그 화면이 그려져 터진다 (`TOPIC_LABELS[topic!]`).
              2026-08-28 실기기에서 광고를 닫는 순간 앱이 멈춘 원인이 이것이었다.
              주제가 있을 때만 넘어가고, 없으면 게이트만 걷힌 채 주제 선택으로 남는다.
            */}
            <QuotaGate
              block={quotaBlock}
              onAdWatched={() => {
                if (topic) setStep('pick');
              }}
            />
            <PrimaryButton disabled={!topic || quotaBlocked} onClick={() => setStep('pick')}>
              카드 뽑으러 가기
            </PrimaryButton>
            <HomeLink />
            {/* 화면 맨 아래 — [카드 뽑으러 가기]가 접히지 않아야 하므로 그 위로 끼워 넣지 않는다 */}
            <TossBanner />
          </motion.section>
        )}

        {/*
          `topic`을 함께 본다 — 이 화면은 주제가 있어야만 성립한다(칩·스프레드가 주제에서 나온다).
          어떤 경로로든 주제 없이 여기 도달하면 화면이 통째로 죽으므로, 조건에 못 박아 둔다.
        */}
        {step === 'pick' && topic && (
          <motion.section key="pick" {...fade} className="flex flex-col gap-4">
            {/*
              헤더: 뒤로가기(좌) + 주제 칩(중앙).

              토스 미니앱에서는 **화살표를 그리지 않는다.** 토스가 얹는 상단 바에 이미 뒤로가기가
              있어서, 화살표를 하나 더 두면 같은 화면에 뒤로가기가 둘이 되고 번들 심사에서
              반려된다 (2026-09-10 실제 반려 사유, doc/apps-in-toss-setup.md §4-9).

              그렇다고 지우기만 하면 주제를 다시 고를 길이 사라진다 — 토스의 뒤로가기는 화면 단위라
              여기서 누르면 주제 선택이 아니라 홈으로 나간다. 그래서 주제 칩 자체를 버튼으로 만든다.
            */}
            <div className="relative flex items-center justify-center py-1">
              {!isToss && (
                <button
                  onClick={backToTopic}
                  aria-label="주제 선택으로 돌아가기"
                  className="absolute left-0 flex h-10 w-10 items-center justify-center rounded-full border border-gold/25 bg-white/5 text-gold/80 transition hover:border-gold/60 hover:bg-gold/10 hover:text-gold"
                >
                  <BackIcon className="h-5 w-5" />
                </button>
              )}
              {isToss ? (
                <button
                  onClick={backToTopic}
                  aria-label="주제 다시 고르기"
                  className={`${TOPIC_CHIP_CLASS} transition hover:bg-gold/20`}
                >
                  <TopicIcon topic={topic!} className="h-9 w-9 text-gold" />
                  <span className="font-semibold">{TOPIC_LABELS[topic!].ko}</span>
                  <CaretIcon className="h-3 w-3 text-moon-dim" />
                </button>
              ) : (
                <div className={TOPIC_CHIP_CLASS}>
                  <TopicIcon topic={topic!} className="h-9 w-9 text-gold" />
                  <span className="font-semibold">{TOPIC_LABELS[topic!].ko}</span>
                </div>
              )}
            </div>

            <CardFan
              key={`${fanKey}-${spreadId}`}
              count={spread.positions.length}
              positionLabels={positionLabels}
              title={spread.nameKo}
              onComplete={setCards}
            />

            {cards.length === spread.positions.length && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col gap-2"
              >
                {/* MBTI(선택) — 카드를 다 뽑고 결과를 기다리는 시점이라 이탈 부담이 가장 적다 */}
                <MbtiField
                  value={mbtiPick}
                  onChange={(v) => {
                    setMbtiTouched(true);
                    setMbtiPick(v);
                  }}
                  open={mbtiOpen}
                  onToggle={() => setMbtiOpen((o) => !o)}
                  hint={
                    me?.mbti && !mbtiTouched
                      ? '내 정보에 저장된 값이에요'
                      : '알려주시면 말투와 조언 방식을 맞춰드려요'
                  }
                />
                {crisis && <CrisisNotice footer />}
                <QuotaGate block={quotaBlock} onAdWatched={onInterpret} />
                <PrimaryButton ref={ctaRef} disabled={quotaBlocked} onClick={onInterpret}>
                  해석 듣기
                </PrimaryButton>
                <SecondaryButton onClick={reshuffle}>
                  <ShuffleIcon className="h-5 w-5" /> 다시 섞어서 뽑기
                </SecondaryButton>
              </motion.div>
            )}
          </motion.section>
        )}

        {step === 'reading' && (
          <motion.section key="reading" {...fade} className="flex flex-col gap-4">
            {/* 공유 캡처 영역: 덱 + 해석 + 브랜드 서명이 그대로 이미지가 됨 */}
            <div ref={captureRef} className="flex flex-col gap-4 rounded-2xl bg-night p-1">
              <ul className={`grid ${slotGridClass(cards.length)}`}>
                {cards.map((d) => (
                  <li key={d.cardId} className="flex min-w-0 flex-col items-center">
                    <span className={slotLabelClass()}>{positionLabels[d.position]}</span>
                    <TarotCard drawn={d} />
                    <CardMeaning drawn={d} />
                  </li>
                ))}
              </ul>
              <article
                ref={readingRef}
                className="whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/5 p-5 leading-relaxed"
              >
                {text ? (
                  <MagicText text={text} active={busy} onComplete={() => setRevealed(true)} />
                ) : busy ? (
                  <span className="text-moon-dim">카드를 읽는 중…</span>
                ) : null}
              </article>
              <CaptureSignature />
            </div>
            {error && <p className="text-sm text-red-300">⚠️ {error}</p>}
            {/* 본문이 다 드러난 뒤에 노출 — 펼치는 중에 이미지를 저장하면 해석이 잘린다 */}
            {revealed && !error && text && (
              <ReadingActions
                targetRef={captureRef}
                shareData={shareData}
                savedShareId={savedShareId}
                onOther={reset}
              />
            )}
            {revealed && !error && text && <SaveCta shareData={shareData} />}
            {/* 회원에게만, 그리고 토스 미니앱에서만 — 저장 유도와 자리를 다투지 않는다 */}
            {revealed && !error && text && <ReviewCta />}
            {/* 해석을 다 읽은 뒤에만 — 읽는 중에 광고가 끼어들면 흐름이 끊긴다 */}
            {revealed && !error && text && <TossBanner />}
            {/* 실패 시 재시도 — 서버가 차감분을 환불하므로(routes/ai.ts) 무료로 다시 시도할 수 있다.
                뽑아둔 카드는 그대로 두고 해석만 다시 요청한다 (카드까지 버리면 억울하다) */}
            {!busy && error && (
              <ActionStack>
                <PrimaryButton onClick={onInterpret}>다시 해석 듣기</PrimaryButton>
                <SecondaryButton onClick={reset}>다른 유형 운세 보기</SecondaryButton>
              </ActionStack>
            )}
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
}

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.3 },
};
